import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Upload, Save, Loader2, Image as ImageIcon, PenTool, Stamp, Smartphone, MessageSquare, MapPin } from "lucide-react";
import { toast } from "sonner";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function ImageUploader({ label, value, onChange, icon: Icon, testId }) {
  const [preview, setPreview] = useState(value || null);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_000_000) return toast.error("Fichier > 2 Mo");
    const b64 = await fileToBase64(f);
    setPreview(b64);
    onChange(b64);
  };
  return (
    <div className="border-2 border-dashed border-slate-300 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#1E3A8A]">
        <Icon className="h-4 w-4" />{label}
      </div>
      {preview && (
        <img src={preview} alt={label} className="w-full max-h-40 object-contain bg-slate-50 rounded-lg mb-3" />
      )}
      <Input type="file" accept="image/*" onChange={handle} data-testid={testId} />
      {preview && (
        <Button size="sm" variant="ghost" onClick={() => { setPreview(null); onChange(null); }} className="mt-2 text-xs text-red-600">Retirer</Button>
      )}
    </div>
  );
}

export default function SchoolSettingsPage() {
  const [cfg, setCfg] = useState(null);
  const [om, setOm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingOm, setSavingOm] = useState(false);
  const [qrVersion, setQrVersion] = useState(0);
  const [smsStatus, setSmsStatus] = useState(null);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [geo, setGeo] = useState(null);
  const [savingGeo, setSavingGeo] = useState(false);
  const [locating, setLocating] = useState(false);
  const [notifHealth, setNotifHealth] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const loadNotifHealth = async () => {
    try {
      const { data } = await api.get("/admin/notifications/health");
      setNotifHealth(data);
      if (!data.all_ok) toast.warning("Diagnostic : un problème de notifications a été détecté");
    } catch { toast.error("Diagnostic indisponible"); }
  };

  const sendTestEmail = async () => {
    setEmailSending(true);
    setEmailResult(null);
    try {
      const { data } = await api.post("/admin/notifications/test-email", { email: testEmail.trim() });
      setEmailResult(data);
    } catch (e) {
      setEmailResult({ sent: false, reason: e.response?.data?.detail || "Erreur serveur" });
    } finally { setEmailSending(false); }
  };

  const saveGeo = async () => {
    setSavingGeo(true);
    try {
      await api.put("/erp/geo-settings", {
        latitude: parseFloat(geo.latitude), longitude: parseFloat(geo.longitude),
        radius_m: parseFloat(geo.radius_m), gps_required: !!geo.gps_required,
        max_accuracy_m: parseFloat(geo.max_accuracy_m || 150),
      });
      toast.success("Périmètre GPS mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSavingGeo(false); }
  };

  const sendTestSms = async () => {
    setSmsSending(true);
    setSmsResult(null);
    try {
      const { data } = await api.post("/sms/test", { phone: smsPhone });
      setSmsResult({ ok: true, msg: `SMS envoyé ! SID : ${data.sid} (statut : ${data.status})` });
      toast.success("SMS test envoyé");
    } catch (e) {
      setSmsResult({ ok: false, msg: e.response?.data?.detail || "Erreur d'envoi" });
    } finally { setSmsSending(false); }
  };

  const load = async () => {
    try {
      const [{ data }, omRes] = await Promise.all([api.get("/school/config"), api.get("/orange-money/config")]);
      setCfg(data);
      setOm(omRes.data);
      api.get("/sms/status").then(r => setSmsStatus(r.data)).catch(() => {});
      api.get("/erp/geo-settings").then(r => setGeo(r.data)).catch(() => {});
    }
    catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { load(); loadNotifHealth(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/school/config", cfg);
      toast.success("Configuration mise à jour — tous les PDF seront maintenant brandés");
      setQrVersion(v => v + 1); // le logo est intégré au QR Orange Money
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const saveOm = async () => {
    setSavingOm(true);
    try {
      await api.put("/orange-money/config", {
        enabled: om.enabled !== false,
        merchant_code: om.merchant_code,
        merchant_name: om.merchant_name,
        country: om.country,
        ussd_code: om.ussd_code,
      });
      toast.success("Configuration Orange Money mise à jour");
      setQrVersion(v => v + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSavingOm(false); }
  };

  if (!cfg) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="space-y-6 max-w-5xl" data-testid="school-settings">
      <div>
        <div className="chip mb-2"><Building2 className="h-3 w-3" /><span>PARAMÉTRAGE ÉCOLE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Configuration de l&apos;école</h1>
        <p className="text-slate-500 mt-1">Logo, signature, cachet et infos légales — utilisés sur tous les imprimés (reçus, bulletins, manuels, certificats).</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
        <div className="font-heading font-bold text-[#1E3A8A]">📋 Informations générales</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Nom de l&apos;école *</Label><Input value={cfg.name || ""} onChange={e => set("name", e.target.value)} data-testid="sc-name" /></div>
          <div><Label>Slogan</Label><Input value={cfg.slogan || ""} onChange={e => set("slogan", e.target.value)} data-testid="sc-slogan" /></div>
          <div><Label>Adresse</Label><Input value={cfg.address || ""} onChange={e => set("address", e.target.value)} /></div>
          <div><Label>Ville</Label><Input value={cfg.city || ""} onChange={e => set("city", e.target.value)} /></div>
          <div><Label>Pays</Label><Input value={cfg.country || ""} onChange={e => set("country", e.target.value)} /></div>
          <div><Label>Téléphone</Label><Input value={cfg.phone || ""} onChange={e => set("phone", e.target.value)} /></div>
          <div><Label>Email</Label><Input value={cfg.email || ""} onChange={e => set("email", e.target.value)} /></div>
          <div><Label>Site web</Label><Input value={cfg.website || ""} onChange={e => set("website", e.target.value)} /></div>
          <div><Label>RCCM</Label><Input value={cfg.rccm || ""} onChange={e => set("rccm", e.target.value)} placeholder="Ex : GN-CKY-2020-B-12345" data-testid="sc-rccm" /></div>
          <div><Label>NIF</Label><Input value={cfg.nif || ""} onChange={e => set("nif", e.target.value)} placeholder="N° identification fiscale" data-testid="sc-nif" /></div>
          <div><Label>Date de la rentrée scolaire</Label><Input type="date" value={cfg.rentree_date || ""} onChange={e => set("rentree_date", e.target.value)} data-testid="sc-rentree" /></div>
          <div><Label>Compte bancaire</Label><Input value={cfg.bank_account || ""} onChange={e => set("bank_account", e.target.value)} /></div>
          <div><Label>Devise</Label><Input value={cfg.currency || "GNF"} onChange={e => set("currency", e.target.value)} /></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
        <div className="font-heading font-bold text-[#1E3A8A]">🖼️ Éléments visuels des imprimés</div>
        <div className="text-sm text-slate-500 mb-2">Ces éléments apparaîtront sur TOUS les documents PDF générés (reçus, bulletins, certificats, manuels).</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ImageUploader label="Logo école" icon={ImageIcon} value={cfg.logo_base64} onChange={v => set("logo_base64", v)} testId="upload-logo" />
          <ImageUploader label="Signature autorisée" icon={PenTool} value={cfg.signature_base64} onChange={v => set("signature_base64", v)} testId="upload-sig" />
          <ImageUploader label="Cachet école" icon={Stamp} value={cfg.stamp_base64} onChange={v => set("stamp_base64", v)} testId="upload-stamp" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div><Label>Nom du signataire</Label><Input value={cfg.signature_name || ""} onChange={e => set("signature_name", e.target.value)} placeholder="Ex : M. Ibrahim MAZID" data-testid="sc-sig-name" /></div>
          <div><Label>Titre / fonction</Label><Input value={cfg.signature_title || ""} onChange={e => set("signature_title", e.target.value)} placeholder="Ex : Fondateur & DG" data-testid="sc-sig-title" /></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border-2 border-[#FF7900]/30 p-6 shadow-sm space-y-4" data-testid="om-settings-card">
        <div className="font-heading font-bold text-[#FF7900] flex items-center gap-2"><Smartphone className="h-4 w-4" />Orange Money — Code marchand</div>
        {om && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
            <div className="space-y-3">
              <div><Label>Code marchand *</Label><Input value={om.merchant_code || ""} onChange={e => setOm({ ...om, merchant_code: e.target.value })} data-testid="om-code" /></div>
              <div><Label>Nom du marchand *</Label><Input value={om.merchant_name || ""} onChange={e => setOm({ ...om, merchant_name: e.target.value })} data-testid="om-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code USSD</Label><Input value={om.ussd_code || ""} onChange={e => setOm({ ...om, ussd_code: e.target.value })} placeholder="*144#" /></div>
                <div><Label>Pays</Label><Input value={om.country || ""} onChange={e => setOm({ ...om, country: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={om.enabled !== false} onChange={e => setOm({ ...om, enabled: e.target.checked })} data-testid="om-enabled" />
                Activer Orange Money comme moyen de paiement (visible côté parents)
              </label>
              <Button onClick={saveOm} disabled={savingOm} className="bg-[#FF7900] hover:bg-orange-600 text-white gap-2" data-testid="om-save">
                {savingOm ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" />Enregistrer Orange Money</>}
              </Button>
            </div>
            <div className="flex flex-col items-center gap-2">
              <img src={`${api.defaults.baseURL}/orange-money/qrcode?v=${qrVersion}`} alt="QR Orange Money"
                   className="w-48 rounded-xl border border-orange-200 shadow" data-testid="om-qr-preview" />
              <div className="text-xs text-slate-500 text-center">QR code affiché aux parents.<br />Le logo de l&apos;école (ci-dessus) est intégré au centre du QR.</div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-3" data-testid="sms-settings-card">
        <div className="font-heading font-bold text-[#1E3A8A] flex items-center gap-2"><MessageSquare className="h-4 w-4" />SMS Twilio — Test d&apos;envoi</div>
        <div className="text-sm text-slate-500">
          {smsStatus?.configured
            ? <>Configuré ✅ — Expéditeur : <strong>{smsStatus.sender_id}</strong>. Les SMS sont envoyés pour le code 2FA et les confirmations de paiement.</>
            : "Twilio non configuré."}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-2">
            <Label>Numéro de test (ex : 622123456 ou +224622123456)</Label>
            <Input value={smsPhone} onChange={e => setSmsPhone(e.target.value)} placeholder="+224 6XX XX XX XX" data-testid="sms-test-phone" />
          </div>
          <Button onClick={sendTestSms} disabled={smsSending || !smsPhone.trim()} className="bg-[#1E3A8A] hover:bg-blue-900 text-white gap-2" data-testid="sms-test-send">
            {smsSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageSquare className="h-4 w-4" />Envoyer un SMS test</>}
          </Button>
        </div>
        {smsResult && <div className={`text-sm rounded-lg p-3 ${smsResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid="sms-test-result">{smsResult.msg}</div>}
      </div>

      {/* Diagnostic notifications (Email & SMS) */}
      <div className="bg-white rounded-2xl border-2 border-amber-300 p-6 shadow-sm space-y-4" data-testid="notif-health-card">
        <div className="flex items-center justify-between">
          <div className="font-heading font-bold text-amber-800">🩺 Diagnostic notifications (codes OTP, emails, SMS)</div>
          <Button variant="outline" size="sm" onClick={loadNotifHealth} data-testid="notif-health-refresh">Vérifier maintenant</Button>
        </div>
        {notifHealth && (
          <>
            <div className={`text-sm font-bold rounded-lg p-3 ${notifHealth.all_ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid="notif-health-status">
              {notifHealth.all_ok ? "✅ Tout est opérationnel — les utilisateurs reçoivent leurs codes." : "⚠️ Problème détecté — les notifications peuvent ne pas partir !"}
            </div>
            <div className="space-y-2">
              {notifHealth.checks.map((c) => (
                <div key={c.key} className="text-sm flex items-start gap-2">
                  <span>{c.ok ? "✅" : "❌"}</span>
                  <div>
                    <span className="text-slate-700">{c.label}</span>
                    {c.hint && <div className="text-xs text-red-600">{c.hint}</div>}
                  </div>
                </div>
              ))}
            </div>
            {notifHealth.last_sms?.length > 0 && (
              <div className="text-xs text-slate-500">
                Derniers SMS : {notifHealth.last_sms.map((s, i) => (
                  <span key={i} className={`inline-block mr-2 px-2 py-0.5 rounded-full ${s.ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`} title={s.error || ""}>
                    {s.to?.slice(-4)} {s.ok ? "✓" : `✗ ${String(s.error || "").slice(0, 40)}`}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end border-t border-slate-100 pt-4">
          <div className="sm:col-span-2">
            <Label>Envoyer un email de test à</Label>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="votre.email@gmail.com" data-testid="email-test-input" />
          </div>
          <Button onClick={sendTestEmail} disabled={emailSending || !testEmail.trim()} className="bg-amber-600 hover:bg-amber-700 text-white gap-2" data-testid="email-test-send">
            {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer l'email test"}
          </Button>
        </div>
        {emailResult && <div className={`text-sm rounded-lg p-3 ${emailResult.sent ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid="email-test-result">
          {emailResult.sent ? "✅ Email de test envoyé — vérifiez la boîte de réception (et les spams)." : `❌ Échec : ${emailResult.reason}`}
        </div>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="font-heading font-bold text-[#1E3A8A] mb-3">🎨 Couleurs de marque</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Couleur primaire</Label>
            <div className="flex gap-2">
              <Input type="color" value={cfg.primary_color || "#1E3A8A"} onChange={e => set("primary_color", e.target.value)} className="w-16 h-10 p-1" />
              <Input value={cfg.primary_color || "#1E3A8A"} onChange={e => set("primary_color", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Couleur secondaire</Label>
            <div className="flex gap-2">
              <Input type="color" value={cfg.secondary_color || "#EA580C"} onChange={e => set("secondary_color", e.target.value)} className="w-16 h-10 p-1" />
              <Input value={cfg.secondary_color || "#EA580C"} onChange={e => set("secondary_color", e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {geo && (
        <div className="bg-white rounded-2xl border-2 border-[#166534]/30 p-6 shadow-sm space-y-4" data-testid="geo-settings-card">
          <div className="font-heading font-bold text-[#166534]">📍 Périmètre GPS du pointage</div>
          <p className="text-xs text-slate-500">Le personnel ne peut pointer que s&apos;il se trouve physiquement dans ce périmètre. Rendez-vous <strong>à l&apos;école</strong>, cliquez « Utiliser ma position actuelle », puis enregistrez.</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                if (!navigator.geolocation) return toast.error("Géolocalisation indisponible sur cet appareil");
                setLocating(true);
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    setGeo({ ...geo, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) });
                    setLocating(false);
                    toast.success(`Position capturée (précision ±${Math.round(pos.coords.accuracy)} m) — cliquez Enregistrer`);
                  },
                  () => { setLocating(false); toast.error("Autorisez la géolocalisation pour capturer la position de l'école"); },
                  { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
                );
              }}
              disabled={locating}
              className="border-[#1E3A8A] text-[#1E3A8A] gap-2"
              data-testid="geo-use-my-position"
            >
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
              Utiliser ma position actuelle
            </Button>
            <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">⚠️ À faire depuis l&apos;enceinte de l&apos;école, GPS activé</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label>Latitude</Label>
              <Input value={geo.latitude} onChange={e => setGeo({ ...geo, latitude: e.target.value })} data-testid="geo-lat" />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input value={geo.longitude} onChange={e => setGeo({ ...geo, longitude: e.target.value })} data-testid="geo-lng" />
            </div>
            <div>
              <Label>Rayon autorisé (mètres)</Label>
              <Input type="number" value={geo.radius_m} onChange={e => setGeo({ ...geo, radius_m: e.target.value })} data-testid="geo-radius" />
            </div>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!geo.gps_required} onChange={e => setGeo({ ...geo, gps_required: e.target.checked })} className="rounded" data-testid="geo-required" />
              Exiger la position GPS pour le pointage
            </label>
            <a href={`https://www.openstreetmap.org/?mlat=${geo.latitude}&mlon=${geo.longitude}#map=18/${geo.latitude}/${geo.longitude}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A8A] hover:underline">Vérifier sur la carte ↗</a>
            <Button onClick={saveGeo} disabled={savingGeo} variant="outline" className="border-[#166534] text-[#166534] gap-1" data-testid="geo-save">
              {savingGeo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Enregistrer le périmètre
            </Button>
          </div>
        </div>
      )}

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2 shadow-lg" data-testid="sc-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" />Enregistrer la configuration</>}
        </Button>
      </div>
    </div>
  );
}
